import { useCallback, useContext } from 'react';
import { useApolloClient } from '@apollo/client';
import 'moment-timezone';
import type { EventTimeline } from '../../core/Timelines';
import type { PortCallData } from '../../core/Core';
import type {
  PortCallBunkerOverride,
  PortCallEntry,
} from '@seaber/common/core/entries';
import { setIntersection } from '../../../lib/util';
import { getTimelinePortCalls } from '../shipment';
import type { ScenarioLocation } from '../../../lib/planningData';
import type { ClientId } from '../../core/ClientId';
import {
  hasFeature,
  useCurrencyRates,
  useDefaultCurrency,
} from '../../../state/zustand';
import type { PnlData } from '../../hooks/usePnlData';
import type { DefaultMap } from '@seaber/common/lib/collections';
import { getCostSharingDetailsForPortCalls } from '../TCE';
import { type DefaultPortCostEstimates } from './portCostDefault';
import { getPortCosts } from './portCost';
import { type HistoricalPortCostEstimates } from './portCostHistory';
import { type ChannelCostByChanel } from './channelCost';
import {
  getExternalPortCostPredictions,
  type PortCostPredictionByPortCall,
} from './portCostPrediction';
import { z } from 'zod';
import CalculationContext from '../../contexts/CalculationContext';
import { useMarcuraPortCosts } from '../../marcura/hooks';

type CostParamSource =
  | 'SEABER'
  | 'EXTERNAL'
  | 'MANUAL'
  | 'cnbc'
  | 'NAPA'
  | 'INTEGRATION';

export const DEFAULT_PORT_COST_PRIORITY = {
  portCostOverride: 1,
  portCostDefault: 2,
  portCostIntegration: 3,
  portCostHistory: 4,
};

export const portCostPrioritySchema = z.object({
  portCostOverride: z.number().optional(),
  portCostIntegration: z.number().optional(),
  portCostDefault: z.number().optional(),
  portCostHistory: z.number().optional(),
});

export type PortCostPriority = z.infer<typeof portCostPrioritySchema>;

export type CostParam = {
  name: string;
  value: string | number | null;
  source: CostParamSource;
  testId?: string;
};

export type BunkerGradeParams = Omit<PortCallBunkerOverride, 'bunker_grade_id'>;

export type Cost =
  | {
      type: 'BUNKER';
      name: string;
      value: number;
      params: CostParam[];
      locations: ScenarioLocation[];
      numShipments: number;
      portCallEntry: PortCallEntry | null;
      bunkerGradeParams?: BunkerGradeParams;
    }
  | {
      type: 'INVOICE_FREIGHT';
      name: string;
      value: number;
      params: CostParam[];
      locations: ScenarioLocation[];
      numShipments: number;
      bunkerGradeParams?: BunkerGradeParams;
    }
  | {
      type: 'PORT_CALL';
      name: string;
      value: number;
      params: CostParam[];
      locations: ScenarioLocation[];
      numShipments: number;
      options?: {
        defaultValue?: DefaultPortCostEstimates; // from port_cost_default
        historicalValue?: HistoricalPortCostEstimates; // from port_cost_estimate_external
        integrationCost?: { value: number; currency: string };
        predictionCost?: { value: number; params: CostParam[] }; // from Neste API
        oneTimeOverrideCost?: number | null;
        portCallEntry: PortCallEntry;
        selectedId?: number | null;
        useHistoricalPortCost?: boolean;
        useOneTimeOverrideCost?: boolean;
        useIntegrationPortCost?: boolean;
        coaPortCost?: { value: number; currency: string };
        selected?: 'default' | 'integration' | 'historical' | 'override';
      };
      useActualPortCost?: boolean;
      isExternalPrediction?: boolean;
    }
  | {
      type: 'CHANNEL';
      name: string;
      value: number;
      locations: Partial<ScenarioLocation>[];
      numShipments: number;
      options: {
        portCallEntry: PortCallEntry;
        oneTimeOverrideChannelCost?: number | null;
        selectedId?: number | null;
        channelCostByChanel: ChannelCostByChanel | undefined;
      };
    }
  | {
      type: 'EXTRA_COST';
      name: string;
      value: number;
      locations: [];
      numShipments: number;
      params: CostParam[];
    };

export type PortCallCost = Extract<Cost, { type: 'PORT_CALL' }>;

export type CostsByPortCall = Map<PortCallData['id'], Cost[]>;

type ShipmentsByPortCallId = DefaultMap<ClientId | null, Set<ClientId | null>>;

export const usePortCallCosts = (pnlData: PnlData) => {
  const apolloClient = useApolloClient();
  const currencyRates = useCurrencyRates();
  const isExternalPortCostPredictionsAvailable = hasFeature('s5_integration');
  const portCostPriority =
    useContext(CalculationContext)?.calculationConfig?.portCostPriority;
  const baseCurrency = useDefaultCurrency();
  const { portCosts: portCostMarcuraMaps } = useMarcuraPortCosts();

  const getTimelinePortCallCosts = useCallback(
    async (
      timeline: EventTimeline,
      shipmentsByPortCallId: ShipmentsByPortCallId,
      focusedShipments: Set<ClientId>,
    ): Promise<{
      errors: string[];
      portCallCosts: CostsByPortCall | null;
    } | null> => {
      if (timeline.type !== 'tc' || !timeline.vessel) {
        return null;
      }

      const costSharingDetailsByPortCall = getCostSharingDetailsForPortCalls(
        timeline.parent?.voyageGroups.flatMap((vg) => vg.events) ??
          timeline.voyageGroups.flatMap((vg) => vg.events),
      );

      const { locationById, portCalls } = getTimelinePortCalls(timeline);

      const errors: string[] = [];

      // Get external predictions if available
      let portCostPredictions: PortCostPredictionByPortCall | null = null;

      if (isExternalPortCostPredictionsAvailable) {
        const { portCostPredictionByPortCall, errors: fetchErrors } =
          await getExternalPortCostPredictions(
            portCalls,
            locationById,
            timeline.vessel,
            apolloClient,
            shipmentsByPortCallId,
          );

        if (fetchErrors.length > 0) {
          errors.push(...fetchErrors);
        }

        portCostPredictions = portCostPredictionByPortCall;
      }

      // Get port cost estimates first (includes default, history, integration)
      const portCallCosts = getPortCosts({
        currencyRates,
        baseCurrency,
        locationById,
        portCalls,
        portCostByVcMaps: pnlData.portCostByVcMaps,
        portCostEstimateMaps: pnlData.portCostEstimateMaps,
        portCostByCoaMaps: pnlData.portCostByCoaMaps,
        portCostMarcuraMaps,
        portCostPredictions,
        shipmentsByPortCallId,
        vessel: timeline.vessel,
        voyageGroups: timeline.voyageGroups,
      });

      for (const [, costs] of portCallCosts) {
        applyPrioritiesToCosts(costs, portCostPriority);
      }

      // Filter out costs for port calls that the focused shipments have no cargo operations at
      if (portCallCosts && focusedShipments.size > 0) {
        for (const portCallId of portCallCosts.keys()) {
          if (portCallId === null) continue;

          const costSharingDetails =
            costSharingDetailsByPortCall.get(portCallId);
          if (!costSharingDetails) continue;

          if (
            setIntersection([
              focusedShipments,
              costSharingDetails.shipmentsWithCargoOperations,
            ]).size < 1
          ) {
            portCallCosts.delete(portCallId);
          }
        }
      }

      return { portCallCosts, errors };
    },
    [
      apolloClient,
      currencyRates,
      isExternalPortCostPredictionsAvailable,
      pnlData.portCostByVcMaps,
      pnlData.portCostEstimateMaps,
      pnlData.portCostByCoaMaps,
      portCostPriority,
      baseCurrency,
      portCostMarcuraMaps,
    ],
  );

  return {
    getTimelinePortCallCosts,
  };
};

/**
 * Applies priority rules to determine which cost source to select for PORT_CALL costs
 */
function applyPrioritiesToCosts(
  costs: Cost[],
  portCostPriority: PortCostPriority = DEFAULT_PORT_COST_PRIORITY,
): void {
  for (const cost of costs) {
    if (cost.type !== 'PORT_CALL' || !cost.options) continue;
    const { options, numShipments } = cost;

    const hasOverride = options.oneTimeOverrideCost !== null;
    const hasIntegration = !!options.integrationCost;
    const hasHistory = !!options.historicalValue;
    const hasDefault = !!options.defaultValue;
    const hasPrediction = !!options.predictionCost;

    type CostSourceWithPriority = {
      // Neste API prediction is considered as integration port cost
      source: 'override' | 'integration' | 'historical' | 'default';
      priority: number;
      value: number;
    };

    const availableSources: CostSourceWithPriority[] = [];

    if (hasOverride) {
      availableSources.push({
        source: 'override',
        priority: options.useOneTimeOverrideCost
          ? -1
          : portCostPriority.portCostOverride || +Infinity,
        value: options.oneTimeOverrideCost! / numShipments,
      });
    }

    if (hasIntegration || hasPrediction) {
      availableSources.push({
        source: 'integration',
        priority: options.useIntegrationPortCost
          ? -1
          : portCostPriority.portCostIntegration || +Infinity,
        value:
          (options.integrationCost?.value ||
            options.predictionCost?.value ||
            0) / numShipments,
      });
    }

    if (hasHistory) {
      availableSources.push({
        source: 'historical',
        priority: options.useHistoricalPortCost
          ? -1
          : portCostPriority.portCostHistory || +Infinity,
        value: options.historicalValue!.cost / numShipments,
      });
    }

    if (hasDefault) {
      const defaultValue =
        (options.selectedId && options.defaultValue?.byId?.cost) ??
        options.defaultValue?.byName?.cost ??
        options.defaultValue?.byClass?.cost ??
        options.defaultValue?.byTypes?.[0]?.cost ??
        0;

      availableSources.push({
        source: 'default',
        priority: options.portCallEntry.portCostDefaultId
          ? -1
          : portCostPriority.portCostDefault || +Infinity,
        value: defaultValue,
      });
    }

    // Sort by priority (lower numbers are higher priority)
    availableSources.sort((a, b) => a.priority - b.priority);

    if (availableSources.length > 0) {
      const selectedSource = availableSources[0];
      options.selected = selectedSource.source;
      cost.value = selectedSource.value;

      // Reset selectedId if we're not using a default cost
      if (
        selectedSource.source !== 'default' &&
        selectedSource.source !== 'override'
      ) {
        options.selectedId = null;
      }
    }
  }
}
